<template>
  <div>
    <header class="group-header">
      <div class="group-tabs-row">
        <div class="tabs is-toggle is-small mb-0">
          <ul>
            <li
              :class="{ 'is-active': selectedTab === 'form' }"
              @click="selectedTab = 'form'"
            >
              <a>
                <span class="icon is-small">
                  <i class="fas fa-plus" aria-hidden="true"></i>
                </span>
                <span>New samples</span>
              </a>
            </li>
            <li
              :class="{ 'is-active': selectedTab === 'list' }"
              @click="selectedTab = 'list'"
            >
              <a>
                <span class="icon is-small">
                  <i class="fas fa-list" aria-hidden="true"></i>
                </span>
                <span>Existing samples</span>
              </a>
            </li>
          </ul>
        </div>
        <span v-if="sampleCount > 0" class="tag is-primary is-small">
          {{ sampleCount }} {{ sampleCount === 1 ? 'sample' : 'samples' }}
        </span>
      </div>
      <div class="group-name-input">
        <label class="label is-small mb-0">Group name</label>
        <input
          v-model="sampleGroupName"
          placeholder="Sample Group Name"
          class="input is-small"
          :class="{ 'is-danger': hasValidationErrors }"
        />
        <p v-if="hasValidationErrors" class="is-size-7 has-text-danger">
          {{ validationErrors[0].$message }}
        </p>
      </div>
    </header>
    <CreateSamples
      v-if="selectedTab === 'form'"
      :key="`create-samples-${selectedTab}`"
      :init-type-id="sampleType"
      @new-samples="samples = $event"
    ></CreateSamples>
    <ListComponent
      v-else-if="selectedTab === 'list'"
      :key="`list${sampleType}`"
      app-name="stocks"
      model-name="samples"
      :model-type="sampleType.toLowerCase()"
      vuex-prefix="registerassay-"
      :is-compact="true"
      :show-add-controls="false"
      :show-global-search="true"
      :show-owner-controls="true"
      :can-save-settings="false"
      init-ownership="personal"
      style="min-height: 590px"
      @selected="samples = setSelectedSamples($event)"
    />
  </div>
</template>
<script>
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { v4 as uuid } from 'uuid'
import { computed, provide, ref, watch } from 'vue'

import { useSample } from '@/composables/useSample'

import CreateSamples from '@/components/CreateSamples.vue'

export default {
  name: 'CreateSampleGroup',
  components: { CreateSamples },
  props: {
    sampleType: { type: String },
  },
  setup(props, { emit }) {
    provide('linkBehavior', 'blank')
    const selectedTab = ref('form')
    const samples = ref({})
    const sampleCount = computed(() => Object.keys(samples.value).length)
    const sampleGroupName = ref('')
    const v$ = useVuelidate(
      {
        sampleGroupName: {
          required: helpers.withMessage(
            'Sample group name is required',
            required,
          ),
        },
      },
      { sampleGroupName },
      {
        registerAs: 'sampleGroupName',
        $scope: 'sampleGroupValidation',
      },
    )
    const validationErrors = computed(
      () => v$?.value?.sampleGroupName?.$errors || [],
    )
    const hasValidationErrors = computed(
      () => validationErrors.value.length > 0,
    )
    watch(selectedTab, () => (samples.value = {}))
    watch(sampleGroupName, () => v$?.value?.$touch(), { immediate: true })

    function setSelectedSamples(s) {
      // Transform the array of selected samples from ListComponent into
      // an object gathering samples by id
      return s.reduce(
        (samplesById, sample) => ({
          ...samplesById,
          [sample.id.value]: sample,
        }),
        {},
      )
    }

    const { sampleIdFieldsNames } = useSample(props.sampleType)
    const sampleGroup = computed(() => {
      const id = uuid()
      return {
        ...sampleIdFieldsNames.value.reduce(
          (idFields, fieldName) => ({
            ...idFields,
            [fieldName]: { value: `${fieldName}:${id}` },
          }),
          {},
        ),
        samples: samples.value,
        name: sampleGroupName.value,
        id,
      }
    })

    watch(sampleGroup, (group) => {
      const isOk = !hasValidationErrors.value && sampleCount.value > 1
      const emittedGroup = isOk ? group : null
      emit('new-group', emittedGroup)
    })

    return {
      v$,
      validationErrors,
      hasValidationErrors,
      sampleGroupName,
      sampleGroup,
      selectedTab,
      samples,
      sampleCount,
      sampleIdFieldsNames,
      setSelectedSamples,
    }
  },
}
</script>
<style scoped lang="scss">
.group-header {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.group-tabs-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.group-name-input {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
</style>
